/**
 * Applying a tenant's chosen font pairing to the live site. The pairing data
 * itself (ids/labels/family stacks/Google Fonts URLs) now lives upstream in
 * `@kevlarcon/pages` (`FONT_PAIRINGS`/`getFontPairing`) since it has no
 * OCEN-specific vocabulary — this module keeps only `applyFontPairing`,
 * which points ocen.css's own `--serif`/`--serif-sc`/`--sans` variables at
 * whichever pairing is active, since those variable names are sgokit's own.
 *
 * `id`s must stay in sync with `accounts.theme.FONT_PAIRING_CHOICES` on the
 * backend, which is the authority on which ids are valid to persist.
 *
 * `cormorant-manrope` matches the fonts already hardcoded in index.html, so
 * existing tenants render unchanged until they opt into a different pairing.
 */
import { getFontPairing } from '@kevlarcon/pages';

export { FONT_PAIRINGS, DEFAULT_FONT_PAIRING_ID, getFontPairing } from '@kevlarcon/pages';
export type { FontPairing } from '@kevlarcon/pages';

const FONT_LINK_ID = 'org-font-pairing';

/** Loads the pairing's Google Fonts stylesheet and points ocen.css's --serif/
 * --serif-sc/--sans variables at its families. */
export function applyFontPairing(id: string): void {
  const pairing = getFontPairing(id);

  let link = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== pairing.googleFontsHref) {
    link.href = pairing.googleFontsHref;
  }

  const root = document.documentElement.style;
  root.setProperty('--serif', pairing.heading);
  root.setProperty('--serif-sc', pairing.heading);
  root.setProperty('--sans', pairing.body);
}
